import { createSlice } from "@reduxjs/toolkit";
import { Category } from "../config";
import {
  doAddCategory,
  doDelCategory,
  doFetchCategoryList,
  doPinCategory,
  doReorderCategory,
  doUpdateCategory,
} from "./category.action";

interface Meta {
  totalItems: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

interface categoryState {
  categories: Category[];
  searchQuery: string;
  meta: Meta | null;
  loading: boolean;
  error: string | null;
}

const initialState: categoryState = {
  categories: [] as Category[],
  searchQuery: "",
  meta: null,
  loading: false,
  error: null,
};

export const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch category list
    builder
      .addCase(doFetchCategoryList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchCategoryList.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchCategoryList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // add category
    builder
      .addCase(doAddCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doAddCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(doAddCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // delete category
    builder
      .addCase(doDelCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doDelCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        state.categories = state.categories.filter(
          (category) => category.pcid !== action.meta.arg
        );
        if (state.meta) {
          state.meta.totalItems -= 1;
        }
      })
      .addCase(doDelCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

      builder
      .addCase(doPinCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(doPinCategory.fulfilled, (state, action) => {
        const { id, isPinned } = action.payload;
        const category = state.categories.find((cat) => cat.pcid === id);
        if (category) {
          category.isPinned = isPinned;
        }
        state.loading = false;
      })
      .addCase(doPinCategory.rejected, (state) => {
        state.loading = false;
      });


    // update
    builder
      .addCase(doUpdateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doUpdateCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const index = state.categories.findIndex(
          (category) => category.pcid === action.payload.id
        );

        if (index !== -1) {
          state.categories[index] = {
            ...state.categories[index],
            ...action.payload.data,
          };
        }
      })
      .addCase(doUpdateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

      builder
      .addCase(doReorderCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doReorderCategory.fulfilled, (state, action) => {
        state.loading = false;
        const newOrder = action.payload;
        const categoryMap = new Map(state.categories.map(cat => [cat.pcid, cat]));
        state.categories = newOrder
          .map(item => categoryMap.get(item.pid))
          .filter(Boolean) as Category[];
      })
      .addCase(doReorderCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    
  },
});

export default categorySlice.reducer;
